import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {
	Button,
	Detail,
	FlexColumn,
	FlexRow,
	Icon,
	Label,
	Pill,
	PillVariant,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaButtonSizer} from './NovaButtonSize';
import {NOVA_FILE_ACCEPTED_EXTENSIONS, fileTypeLabel} from '../shared/novaFileTypes';
import {NOVA_FILE_READY_STATUS} from '../shared/novaVariableLibrary';
import {NovaPendingUpload, NovaReferenceFilesProps, formatBytes, useNovaReferenceFiles} from './useNovaReferenceFiles';

const tokens = tokensForThemeProvider.sky.sys;

const WorkflowContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const WorkflowLabelRow = styled.div`
	margin-bottom: ${tokens.spacing['200']};
`;

const WorkflowAcceptedRow = styled.div`
	margin-top: ${tokens.spacing['200']};
`;

const WorkflowDropInvitation = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing['200']};
`;

const WorkflowFileList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['200']};
	margin-top: ${tokens.spacing['300']};
`;

// The drop target doubles as the Browse trigger's surround. A dashed border reads as "drop here"
// without needing a bespoke DS component; it highlights while a file is dragged over it.
const WorkflowDropZone = styled.div<{$active: boolean}>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${tokens.spacing['300']};
	min-height: 160px;
	padding: ${tokens.spacing['600']} ${tokens.spacing['400']};
	border: 1.5px dashed ${({$active}) => ($active ? NovaStudioColor.focusRing : NovaStudioColor.border)};
	border-radius: 8px;
	background-color: ${({$active}) => ($active ? NovaStudioColor.badgeBackground : 'transparent')};
`;

// Badge | filename + size | copy-token and Remove together. The actions column sizes to its content
// because the token has to stay readable in full — it is the text you are about to put on the
// clipboard — so the filename column is the one that gives way, truncating with an ellipsis.
const WorkflowRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	align-items: center;
	column-gap: ${tokens.spacing['300']};
	padding: ${tokens.spacing['200']} ${tokens.spacing['300']};
	border: 1px solid ${NovaStudioColor.border};
	border-radius: 8px;
`;

// Never wrapped: the token is a single copyable string, and breaking it across lines would both
// misrepresent it and make the button grow taller than the one beside it.
const WorkflowToken = styled.span`
	white-space: nowrap;
`;

// A thin bar under the file name while it uploads. The design system has no current progress bar,
// so the track and fill come from the Studio's light blue surface and the primary background colour.
const ProgressTrack = styled.div`
	height: 4px;
	margin-top: ${tokens.spacing['100']};
	border-radius: 999px;
	background-color: ${NovaStudioColor.novaSurface};
	overflow: hidden;
`;

const ProgressFill = styled.div<{$percent: number}>`
	height: 100%;
	width: ${({$percent}) => $percent}%;
	border-radius: 999px;
	background-color: ${tokens.color.background.primary};
	transition: width 0.25s;
`;

const HiddenInput = styled.input`
	display: none;
`;

interface PendingUploadRowProps {
	pending: NovaPendingUpload;
	isBusy: boolean;
	onRetry: () => void;
	onRemove: () => void;
}

// A file still going up: its progress in place of the size, and no variable yet. A failed upload
// says so in place of the progress, with Retry and Remove.
function PendingUploadRow({pending, isBusy, onRetry, onRemove}: PendingUploadRowProps): JSX.Element {
	const {formatMessage} = useIntl();
	const percent = Math.round(pending.progress * 100);
	return (
		<WorkflowRow data-cy={`nova-reference-file-pending-${pending.key}`}>
			<Pill variant={PillVariant.NEUTRAL}>{fileTypeLabel(pending.file.name)}</Pill>
			<FlexColumn gap="xxs" alignItems="flex-start">
				<Text ellipsis>{pending.file.name}</Text>
				{pending.failed ? (
					<Text size="2" color="error" data-cy={`nova-reference-file-failed-${pending.key}`}>
						{formatMessage({id: 'nova.referenceFiles.indexingFailed'})}
					</Text>
				) : (
					<>
						<Detail size="1" color="auxiliary" data-cy={`nova-reference-file-progress-${pending.key}`}>
							{formatMessage({id: 'nova.referenceFiles.indexing'}, {percent})}
						</Detail>
						<ProgressTrack
							role="progressbar"
							aria-valuemin={0}
							aria-valuemax={100}
							aria-valuenow={percent}
							aria-label={pending.file.name}
						>
							<ProgressFill $percent={percent} />
						</ProgressTrack>
					</>
				)}
			</FlexColumn>
			<FlexRow gap="s" alignItems="center">
				{pending.failed && (
					<NovaButtonSizer $size="row">
						<Button
							emphasis="medium"
							data-cy={`nova-reference-file-retry-${pending.key}`}
							disabled={isBusy}
							onPress={onRetry}
						>
							{formatMessage({id: 'nova.referenceFiles.retry'})}
						</Button>
					</NovaButtonSizer>
				)}
				{pending.failed && (
					<NovaButtonSizer $size="row">
						<Button emphasis="medium" data-cy={`nova-reference-file-dismiss-${pending.key}`} onPress={onRemove}>
							{formatMessage({id: 'nova.referenceFiles.remove'})}
						</Button>
					</NovaButtonSizer>
				)}
			</FlexRow>
		</WorkflowRow>
	);
}

// A workflow's Files section on its Prompt tab: the same attach, remove and copy as a task agent's,
// laid out more compactly with smaller buttons and type. Each upload is a row showing its progress,
// and a copied token reads "Copied" on its own button for a moment.
export function NovaWorkflowReferenceFiles({files, onAttach, onRemove}: NovaReferenceFilesProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {
		inputRef,
		dragActive,
		isBusy,
		error,
		copied,
		copyToken,
		remove,
		dropZoneHandlers,
		handleInputChange,
		pendingUploads,
		retryUpload,
		dismissUpload,
	} = useNovaReferenceFiles({onAttach, onRemove, trackUploads: true});

	return (
		<WorkflowContainer data-cy="nova-reference-files" data-testid="nova-reference-files">
			<WorkflowLabelRow>
				<Label>{formatMessage({id: 'nova.referenceFiles.title'})}</Label>
			</WorkflowLabelRow>
			<WorkflowDropZone
				$active={dragActive}
				role="group"
				aria-label={formatMessage({id: 'nova.referenceFiles.title'})}
				data-cy="nova-reference-files-dropzone"
				{...dropZoneHandlers}
			>
				<Icon icon="upload" size="m" color="primary" />
				{/* Each upload shows its own progress as a row below, so the invitation stays; Browse waits
					    until the upload in flight settles, since one file goes up at a time. */}
				<WorkflowDropInvitation>
					<Text>{formatMessage({id: 'nova.referenceFiles.dropHint'})}</Text>
					<NovaButtonSizer $size="row">
						<Button
							emphasis="low"
							data-cy="nova-reference-files-browse"
							disabled={isBusy}
							onPress={() => inputRef.current?.click()}
						>
							{formatMessage({id: 'nova.referenceFiles.browse'})}
						</Button>
					</NovaButtonSizer>
				</WorkflowDropInvitation>
				<HiddenInput
					ref={inputRef}
					type="file"
					accept={NOVA_FILE_ACCEPTED_EXTENSIONS.join(',')}
					data-cy="nova-reference-files-input"
					disabled={isBusy}
					onChange={handleInputChange}
				/>
			</WorkflowDropZone>
			<WorkflowAcceptedRow>
				<Detail size="1" color="auxiliary">
					{formatMessage({id: 'nova.referenceFiles.workflowAccepted'})}
				</Detail>
			</WorkflowAcceptedRow>
			{error && (
				<WorkflowAcceptedRow>
					<Text color="error" data-cy="nova-reference-files-error">
						{error}
					</Text>
				</WorkflowAcceptedRow>
			)}
			{(files.length > 0 || pendingUploads.length > 0) && (
				<WorkflowFileList>
					{pendingUploads.map(pending => (
						<PendingUploadRow
							key={`pending-${pending.key}`}
							pending={pending}
							isBusy={isBusy}
							onRetry={() => retryUpload(pending.key)}
							onRemove={() => dismissUpload(pending.key)}
						/>
					))}
					{files.map(file => (
						<WorkflowRow key={file.fileId} data-cy={`nova-reference-file-${file.fileId}`}>
							<Pill variant={PillVariant.NEUTRAL}>{fileTypeLabel(file.filename)}</Pill>
							<FlexColumn gap="xxs" alignItems="flex-start">
								<Text ellipsis>{file.filename}</Text>
								<Detail size="1" color="auxiliary">
									{formatBytes(file.sizeBytes)}
								</Detail>
							</FlexColumn>
							<FlexRow gap="s" alignItems="center">
								{file.processingStatus === NOVA_FILE_READY_STATUS && (
									// The token itself is the button: one target, labelled with exactly the text it
									// puts on the clipboard.
									// A copied token reads "Copied" on the button itself for a moment.
									<NovaButtonSizer $size="row">
										<Button
											emphasis="medium"
											icon="copyLink"
											data-cy={`nova-reference-file-copy-${file.fileId}`}
											title={formatMessage({id: 'nova.referenceFiles.copy'})}
											onPress={() => copyToken(file)}
										>
											<WorkflowToken>
												{copied?.fileId === file.fileId && copied.ok
													? formatMessage({id: 'nova.referenceFiles.copied'})
													: file.variableToken}
											</WorkflowToken>
										</Button>
									</NovaButtonSizer>
								)}
								<span role="status" data-cy={`nova-reference-file-copy-status-${file.fileId}`}>
									{copied?.fileId === file.fileId && !copied.ok && (
										<Text size="2" color="error">
											{formatMessage({id: 'nova.referenceFiles.copyError'})}
										</Text>
									)}
								</span>
								<NovaButtonSizer $size="row">
									<Button
										emphasis="medium"
										data-cy={`nova-reference-file-remove-${file.fileId}`}
										disabled={isBusy}
										onPress={() => remove(file.fileId)}
									>
										{formatMessage({id: 'nova.referenceFiles.remove'})}
									</Button>
								</NovaButtonSizer>
							</FlexRow>
						</WorkflowRow>
					))}
				</WorkflowFileList>
			)}
		</WorkflowContainer>
	);
}
